import { config } from '@/apollo/config';
import AddImage from '@/assets/images/payment/add-circle.svg';
import CheckedImage from '@/assets/images/payment/tick-circle.svg';
import CustomRadioButton from '@/components/ui/radio-button';
import {
  ExternalPartnerNameEnum,
  PaymentTabEnum,
  useDraftOrderUpdateMutation,
  useOrderApplyBonusesMutation,
  useOrderCancelBonusesMutation,
  usePaymentTabsQuery,
  UserOrdersQuery,
} from '@/graphql';
import convertToReadable from '@/helpers/convertToReadable';
import { useLanguageCode } from '@/hooks/useLanguageCode';
import { useTranslatedDataWithNode } from '@/hooks/useTranslatedData';
import useUserStore from '@/zustand/useUser';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { Button, Message, toaster } from 'rsuite';
import Credit from './credit';
import BonusModal from './modal/bonusModal';
import Promocode from './modal/promocode';
import styles from './styles.module.scss';

interface PaymentProps {
  setState: (e: boolean) => void;
  setPaymentMethod: (e: PaymentTabEnum) => void;
  setInstallmentPeriod: (e: number | null) => void;
  setInstallmentName: (e: ExternalPartnerNameEnum | string) => void;
  data: UserOrdersQuery | undefined;
  onCompleteOrder: () => void;
  setApplicationId: (e: string | null) => void;
  applicationId: string | null;
  loading: boolean;
}

export default function Payment({
  setPaymentMethod,
  setState,
  data,
  onCompleteOrder,
  setInstallmentName,
  setInstallmentPeriod,
  setApplicationId,
  applicationId,
  loading,
}: PaymentProps) {
  const { user } = useUserStore();
  const order = data?.userDraftOrders?.[0];
  const orderId = order?.id;
  const voucher = order?.voucher;
  const serverAppliedBonuses = order?.appliedBonusesAmount;
  const serverAppliedVoucher = order?.voucherCode;
  const serverPaymentMethod = order?.paymentMethodName;
  const [appliedBonuses, setAppliedBonuses] = useState(0);
  const [voucherCode, setVoucherCode] = useState<string | null>(null);
  const [voucherError, setVoucherError] = useState<string | null>(null);
  const languageCode = useLanguageCode();
  useEffect(() => {
    setAppliedBonuses(serverAppliedBonuses ?? 0);
  }, [serverAppliedBonuses]);

  useEffect(() => {
    setVoucherCode(serverAppliedVoucher ?? null);
  }, [serverAppliedVoucher]);

  const [updateDraftOrder, { loading: updateLoading }] =
    useDraftOrderUpdateMutation();
  const [applyBonus, { loading: applyLoading }] =
    useOrderApplyBonusesMutation();
  const [cancelBonus, { loading: cancelLoading }] =
    useOrderCancelBonusesMutation();
  const isLoading = applyLoading || cancelLoading;
  const { data: tabsData, loading: tabsLoading } = usePaymentTabsQuery({
    variables: {
      first: 100,
      languageCode: languageCode,
    },
  });
  console.log('tabsData', tabsData);
  const [selectedOption, setSelectedOption] = useState<number | undefined>(
    undefined
  );
  const [bonusModal, setBonusModal] = useState<any>(false);
  const [promoModal, setPromoModal] = useState<any>(false);
  const handleRadioChange = (event: { target: { value: any } }) => {
    setSelectedOption(event.target.value);
  };

  const isInstallmentSelected =
    tabsData?.paymentTabs?.edges[selectedOption as number]?.node?.method ===
    'INSTALLMENT';

  const handleApplyBonusInModal = async (amount: number) => {
    if (!orderId) {
      console.error('Order ID is missing for applying bonus');
      toaster.push(<Message type="error">ID заказа не найден.</Message>, {
        placement: 'topCenter',
      });
      return;
    }
    if (amount <= 0) {
      toaster.push(
        <Message type="warning">
          Выберите количество бонусов для списания.
        </Message>,
        {
          placement: 'topCenter',
        }
      );
      return;
    }
    try {
      await applyBonus({ variables: { id: orderId, amount } });
    } catch (error) {
      console.error('Error on apply bonus: ', error);
    }
    setAppliedBonuses(amount);
    setBonusModal(false);
  };

  const handleCancelBonus = async () => {
    if (!orderId) {
      console.error('Order ID is missing for cancelling bonus');
      toaster.push(<Message type="error">ID заказа не найден.</Message>, {
        placement: 'topCenter',
      });
      return;
    }
    try {
      await cancelBonus({ variables: { id: orderId } });
    } catch (error) {
      console.error('Error on Cancel bonus: ', error);
    }
    setAppliedBonuses(0);
  };

  const handleApplyVoucher = async (voucherCode: string) => {
    if (!orderId) {
      console.error('Order ID is missing for applying voucher');
      toaster.push(<Message type="error">ID заказа не найден.</Message>, {
        placement: 'topCenter',
      });
      return;
    }
    if (!voucherCode || voucherCode.trim() === '') {
      toaster.push(<Message type="warning">Введите промокод.</Message>, {
        placement: 'topCenter',
      });
      return;
    }

    try {
      const updateRes = await updateDraftOrder({
        variables: {
          id: orderId,
          input: {
            voucherCode: voucherCode.trim(),
          },
        },
      });
      if (updateRes.data?.draftOrderUpdate?.order === null) {
        setVoucherError(updateRes.data?.draftOrderUpdate?.errors?.[0]?.message);
      } else {
        setPromoModal(false);
      }
    } catch (error) {
      console.error('Error on update draft order: ', error);
    }
  };

  const handleCancelVoucher = async () => {
    if (!orderId) {
      console.error('Order ID is missing for cancelling voucher');
      toaster.push(<Message type="error">ID заказа не найден.</Message>, {
        placement: 'topCenter',
      });
      return;
    }

    try {
      await updateDraftOrder({
        variables: {
          id: orderId,
          input: {
            voucherCode: null,
          },
        },
      });
    } catch (error) {
      console.error('Error on update draft order: ', error);
    }
  };

  useEffect(() => {
    const paymentEdges = tabsData?.paymentTabs?.edges;

    if (serverPaymentMethod && paymentEdges && paymentEdges.length > 0) {
      const foundIndex = paymentEdges.findIndex(
        edge => edge?.node?.method === String(serverPaymentMethod)
      );

      if (foundIndex !== -1) {
        if (selectedOption !== foundIndex) {
          setSelectedOption(foundIndex);

          const matchedNode = paymentEdges[foundIndex]?.node;
          if (matchedNode) {
            setPaymentMethod(matchedNode.method);
            if (matchedNode.method !== PaymentTabEnum.INSTALLMENT) {
              setInstallmentName('');
              setInstallmentPeriod(null);
              setApplicationId(null);
            } else {
              setInstallmentName(order?.installmentPartnerName ?? '');
              setInstallmentPeriod(order?.installmentPeriod);
            }
          }
        }
      } else {
        if (selectedOption !== undefined) {
          setSelectedOption(undefined);
          setPaymentMethod(PaymentTabEnum.FREEDOM_PAY);
          setInstallmentName('');
          setInstallmentPeriod(null);
          setApplicationId(null);
        }
      }
    }
  }, [order]);
  const t = useTranslations('Components.payments');
  const translatedTabsData = useTranslatedDataWithNode(
    tabsData?.paymentTabs?.edges,
    ['name', 'description', 'label']
  );
  return (
    <div className={styles.root}>
      <div className={styles.cardsBox}>
        {translatedTabsData?.map((edge, id: number) => {
          const i = edge?.node;
          const images = [i?.image1, i?.image2, i?.image3].filter(Boolean);

          const handleRadioChangeWrapper = (event: {
            target: { value: any };
          }) => {
            handleRadioChange(event);
            setPaymentMethod(i?.method);
          };
          return (
            <div
              className={`${styles.card} ${
                i?.method === 'INSTALLMENT' ? styles.installmentCard : ''
              }`}
              key={id}
            >
              <CustomRadioButton
                checked={Number(selectedOption) === id}
                onChange={handleRadioChangeWrapper}
                value={id}
              >
                <div className={styles.right}>
                  <div className={styles.texts}>
                    <h2 className={styles.title}>{i?.name} </h2>
                    <p className={styles.subTitle}>{i?.description}</p>
                    {images.length > 0 ? (
                      <div className={styles.images}>
                        {images.map((x: any, idx: number) => (
                          <div key={idx} className={styles.imgWrapper}>
                            <Image
                              className={styles.img}
                              src={`${config.publicApiUrl}${x}`}
                              alt="payment"
                              width={40}
                              height={12}
                              style={{ objectFit: 'contain' }}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      ''
                    )}
                  </div>

                  {i?.label ? (
                    <p
                      className={styles.buttonText}
                      style={{ backgroundColor: `${i?.labelColor}` }}
                    >
                      {i?.label}
                    </p>
                  ) : (
                    ''
                  )}
                </div>
              </CustomRadioButton>
            </div>
          );
        })}
      </div>

      {isInstallmentSelected && (
        <Credit
          data={data}
          setInstallmentName={setInstallmentName}
          setInstallmentPeriod={setInstallmentPeriod}
          setApplicationId={setApplicationId}
          applicationId={applicationId}
        />
      )}
      <hr className={styles.line} />

      <div className={styles.sale}>
        <h2 className={styles.saleTitle}>{t('apply-discount')}</h2>
        <div className={styles.saleBox}>
          {voucher ? (
            <div className={styles.saleWrapperApplied}>
              <div className={styles.saleTextWrapper}>
                <div className={styles.checkedSvg}>
                  <CheckedImage />
                </div>
                <div className={styles.promoCodeBox}>
                  <h6 className={styles.promoCodeTitle}>{t('promocode')}</h6>
                  <p className={styles.promoCodeText}>
                    {t('discount')} «{voucher?.name}» {t('applied')}
                  </p>
                  <p
                    className={`${styles.cancelText} ${updateLoading ? styles.disabledLink : ''}`}
                    onClick={!updateLoading ? handleCancelVoucher : undefined}
                  >
                    {updateLoading ? t('undo') : t('cancel')}
                  </p>
                </div>
              </div>
              <div
                className={styles.editBtn}
                onClick={() => setPromoModal(true)}
              >
                {t('change')}
              </div>
            </div>
          ) : (
            <div
              className={styles.saleWrapper}
              onClick={() => setPromoModal(true)}
            >
              <div className={styles.addSvg}>
                <AddImage />
              </div>
              <div className={styles.promoCodeBox}>
                <h6 className={styles.promoCodeTitle}>
                  {t('apply-promocode')}
                </h6>
                <p className={styles.promoCodeText}>{t('type-promocode')}</p>
              </div>
            </div>
          )}

          {appliedBonuses === 0 ? (
            <>
              <div
                className={styles.saleWrapper}
                onClick={() => setBonusModal(true)}
              >
                <div className={styles.addSvg}>
                  <AddImage />
                </div>
                <div className={styles.promoCodeBox}>
                  <h6 className={styles.promoCodeTitle}>
                    {t('apply-self-bonus')}
                  </h6>
                  <p className={styles.promoCodeText}>
                    {t('your-bonuses')}:{' '}
                    {convertToReadable(user?.bonusesAmount)} {t('price')}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className={styles.saleWrapperApplied}>
              <div className={styles.saleTextWrapper}>
                <div className={styles.checkedSvg}>
                  <CheckedImage />
                </div>
                <div className={styles.promoCodeBox}>
                  <h6 className={styles.promoCodeTitle}>
                    {t('applied-bonuses')}
                  </h6>
                  <p className={styles.promoCodeText}>
                    {t('used')} {convertToReadable(appliedBonuses)}{' '}
                    {t('price-from')} {convertToReadable(user?.bonusesAmount)}{' '}
                    {t('price')}
                  </p>
                  <p
                    className={`${styles.cancelText} ${cancelLoading ? styles.disabledLink : ''}`}
                    onClick={!cancelLoading ? handleCancelBonus : undefined}
                  >
                    {cancelLoading ? t('undo') : t('cancel')}
                  </p>
                </div>
              </div>
              <div
                className={styles.editBtn}
                onClick={() => setBonusModal(true)}
              >
                {t('change')}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={styles.footer}>
        <Button
          onClick={() => {
            // setState(false)
            onCompleteOrder();
          }}
          disabled={loading}
          loading={loading}
          className={styles.footerButton}
        >
          {t('formalize')}
        </Button>
      </div>
      {bonusModal && (
        <BonusModal
          open={bonusModal}
          close={() => setBonusModal(false)}
          userBonus={user?.bonusesAmount}
          orderId={orderId}
          onApplyBonus={handleApplyBonusInModal}
          onCancelBonus={handleCancelBonus}
          applyLoading={applyLoading}
          cancelLoading={cancelLoading}
          initialAppliedAmount={appliedBonuses}
        />
      )}
      {promoModal && (
        <Promocode
          open={promoModal}
          close={() => setPromoModal(false)}
          orderId={orderId}
          onApplyVoucher={handleApplyVoucher}
          isLoading={updateLoading}
          voucherError={voucherError}
          setVoucherError={setVoucherError}
          setVoucherCode={setVoucherCode}
          voucherCode={voucherCode}
        />
      )}
    </div>
  );
}
